import axios from "axios";

const apiBaseUrl ="/api";
const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true, // Needed to send refresh cookie for /auth/refresh
});

// ============================
// ACCESS TOKEN (IN MEMORY)
// ============================
let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
  localStorage.setItem("token", token); // persist across reloads
};

export const clearAccessToken = () => {
  accessToken = null;
  localStorage.removeItem("token");
};

// ============================
// REQUEST INTERCEPTOR
// ============================
api.interceptors.request.use(
  (config) => {
    const token = accessToken || localStorage.getItem("token"); // fallback to localStorage
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================
// RESPONSE INTERCEPTOR
// ============================
let refreshPromise = null;

const shouldSkipRefresh = (url = "") => {
  const path = String(url || "");
  return (
    path.includes("/auth/login") ||
    path.includes("/auth/register") ||
    path.includes("/auth/refresh") ||
    path.includes("/auth/logout") ||
    path.includes("/oauth/google/login") ||
    path.includes("/oauth/microsoft/login")
  );
};

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = axios.post(
      `${apiBaseUrl}/auth/refresh`,
      {},
      { withCredentials: true }
    );
  }

  try {
    const response = await refreshPromise;
    const newToken = response?.data?.access_token;
    if (newToken) {
      setAccessToken(newToken);
    }
    return newToken;
  } finally {
    refreshPromise = null;
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const errorCode = error?.response?.data?.code;

    if (status === 401 && errorCode === "ACCOUNT_DEACTIVATED") {
      clearAccessToken();
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
      return Promise.reject(error);
    }

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      shouldSkipRefresh(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
    } catch (refreshError) {
      clearAccessToken();
      return Promise.reject(refreshError);
    }

    clearAccessToken();
    return Promise.reject(error);
  }
);

// ============================
// CONTACT US API
// ============================
export const submitContactUs = async (data) => {
  // data = { subject: "...", message: "..." }
  return api.post("/user/queries", data); // backend endpoint
};

// ============================
// USER PROFILE API
// ============================
export const getUserProfile = async () => {
  return api.get("/user/profile");
};

export const updateUserProfile = async (data) => {
  // data = { first_name, last_name, email, mobile, address }
  return api.put("/user/profile", data);
};

// Master dropdowns and professional details
export const getMasterOptions = () => api.get("/master/options");

export const saveProfessionalDetails = (payload) =>
  api.post("/user/professional-details", payload);

export const getProfessionalDetails = () =>
  api.get("/user/professional-details");

export const getCalendarSetup = () => api.get("/user/setup-calender");

export default api;
